import type { HTMLAttributes } from 'react'
import React from 'react'
import { graphql } from 'babel-plugin-relay/macro'
import { useFragment } from 'react-relay-offline'

import { AssemblyItemSelectCheckbox } from './AssemblyItemSelectCheckbox'
import { AssemblyItemSelectRadio } from './AssemblyItemSelectRadio'
import { getPurchaseConditionPrice } from '../../../../core/utils/getPurchaseConditionPrice'
import { useQuery } from '../../../../core/requests/graphql'
import type { PurchaseConditionPrice } from '../Advertisements/types'
import { getSalesChannel } from '../../../../core/utils/salesChannel'
import { getSimulationParams } from '../../../../core/utils/simulation'
import { useOrder } from '../../../../core/globalState/order'
import { DEFAULT_SKU_IMAGE } from './constants'
import type { AssemblyItemSelect_ProductSkuAssemblyItemCompositionItem$key } from './__generated__/AssemblyItemSelect_ProductSkuAssemblyItemCompositionItem.graphql'

const SIMULATE_ITEMS_QUERY = `query simulateItems($simulationInput: SimulationInput) {
  simulateBySku(simulationInput: $simulationInput) @context(provider: "vtex.instore-chk-graphql") {
    purchaseConditionsPrices{
      id
      seller
      sellerChain
      price
      listPrice
      deliveryChannels
    }
  }
}
`

type SimulateItemsQuery = {
  simulateBySku: {
    purchaseConditionsPrices: PurchaseConditionPrice[] | undefined
    logisticsInfo: LogisticsInfoItem[]
    items: Array<{ id: string }>
  }
}

export type AssemblyItemSelectBaseProps = {
  isRadioInput: boolean
  currentAssemblyItemId: string
  minQuantity: number
  maxQuantity: number
  sellerId: string
}

export type AssemblyItemSelectProps = {
  productSkuAssemblyItemCompositionItem: AssemblyItemSelect_ProductSkuAssemblyItemCompositionItem$key
} & AssemblyItemSelectBaseProps &
  HTMLAttributes<HTMLDivElement>

export const AssemblyItemSelect = ({
  isRadioInput,
  sellerId,
  currentAssemblyItemId,
  minQuantity,
  maxQuantity,
  productSkuAssemblyItemCompositionItem,
}: AssemblyItemSelectProps) => {
  const assemblyItemSelect = useFragment<AssemblyItemSelect_ProductSkuAssemblyItemCompositionItem$key>(
    graphql`
      fragment AssemblyItemSelect_ProductSkuAssemblyItemCompositionItem on ProductSkuAssemblyItemCompositionItem {
        sku {
          _id
          name
          images {
            edges {
              node {
                url
              }
            }
          }
        }
      }
    `,
    productSkuAssemblyItemCompositionItem
  )

  const { order } = useOrder()

  const simulationItems = [
    {
      id: assemblyItemSelect.sku._id,
      seller: sellerId,
      quantity: 1,
    },
  ]

  const shouldFetchSimulateItems = Boolean(
    order.orderForm && simulationItems.length > 0
  )

  // TODO: SA-267 - Change to use the new Simulation APIs
  const { data: simulationData, isValidating } = useQuery<SimulateItemsQuery>(
    {
      query: SIMULATE_ITEMS_QUERY,
      operationName: 'simulateItems',
      revalidateOnMount: true,
      variables: {
        simulationInput: {
          sc: getSalesChannel(),
          items: simulationItems,
          ...getSimulationParams(),
        },
      },
    },
    undefined,
    shouldFetchSimulateItems
  )

  const purchaseConditionPrice = getPurchaseConditionPrice(
    assemblyItemSelect.sku._id,
    simulationData?.simulateBySku.purchaseConditionsPrices
  )

  const hasDiscount =
    (purchaseConditionPrice?.price ?? 0) / 100 !==
    (purchaseConditionPrice?.listPrice ?? 0) / 100

  const currentAssembly = {
    id: assemblyItemSelect.sku._id,
    name: assemblyItemSelect.sku.name,
    imageUrl:
      assemblyItemSelect.sku.images.edges[0]?.node.url ?? DEFAULT_SKU_IMAGE,
    assemblyId: currentAssemblyItemId,
    seller: sellerId,
  }

  const props = {
    purchaseConditionPrice,
    currentAssemblyItemId,
    minQuantity,
    maxQuantity,
    isLoadingSimulation: isValidating,
    sellerId,
    hasDiscount,
    currentAssembly,
  }

  return isRadioInput ? (
    <AssemblyItemSelectRadio {...props} />
  ) : (
    <AssemblyItemSelectCheckbox {...props} isRadioInput={isRadioInput} />
  )
}
